import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc, where, getDoc } from 'firebase/firestore';
import { Logo } from '../components/Logo';

function Allsight() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userData, logout } = useAuth();
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all'); // 'all', 'books', 'ephemera'
  const [sortBy, setSortBy] = useState('newest');
  const [editingBook, setEditingBook] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Duplicate checker state
  const [showDuplicateChecker, setShowDuplicateChecker] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [reassigning, setReassigning] = useState(false);

  const locations = [
    'Non-Fiction',
    'Fiction',
    'MS Fiction',
    'MS Non-Fiction',
    'MS Quick',
    'Graphic Novels',
    'MS Graphic Novels',
    'MS GN Non-Fiction',
    'Easy',
    'Early Non-Fiction',
    'Reference',
    'Spanish'
  ];

  useEffect(() => {
    loadBooks();
  }, [currentUser]);

  useEffect(() => {
    filterAndSortBooks();
  }, [books, searchTerm, filterLocation, filterStatus, filterType, sortBy]);

  // Check URL params for duplicates mode
  useEffect(() => {
    if (searchParams.get('duplicates') === 'true') {
      setShowDuplicateChecker(true);
    }
  }, [searchParams]);

  // Find duplicates whenever books change
  useEffect(() => {
    findDuplicates();
  }, [books]);

  const findDuplicates = () => {
    const barcodeMap = {};
    books.forEach(book => {
      const barcode = book.barcode;
      if (barcode) {
        if (!barcodeMap[barcode]) {
          barcodeMap[barcode] = [];
        }
        barcodeMap[barcode].push(book);
      }
    });
    
    const groups = Object.entries(barcodeMap)
      .filter(([barcode, bookList]) => bookList.length > 1)
      .map(([barcode, bookList]) => ({
        barcode,
        books: bookList.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
        keepIndex: 0  // Default: keep the oldest (index 0)
      }));
    
    setDuplicateGroups(groups);
  };

  const toggleKeepBook = (barcode) => {
    setDuplicateGroups(prev => prev.map(group => {
      if (group.barcode === barcode) {
        // Toggle between 0 and 1 (or cycle through if more than 2)
        const nextIndex = (group.keepIndex + 1) % group.books.length;
        return { ...group, keepIndex: nextIndex };
      }
      return group;
    }));
  };

  const reassignBarcode = async (bookId) => {
    if (!currentUser) return;
    
    setReassigning(true);
    try {
      // Get current highest barcode from user profile
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const lastBarcodeUsed = userDoc.data()?.lastBarcodeUsed || 125863;
      const newBarcode = (lastBarcodeUsed + 1).toString();
      
      // Update the book with new barcode
      const bookRef = doc(db, 'users', currentUser.uid, 'books', bookId);
      await updateDoc(bookRef, { 
        barcode: newBarcode,
        barcodeReassignedAt: new Date(),
        previousBarcode: books.find(b => b.id === bookId)?.barcode
      });
      
      // Update user's lastBarcodeUsed
      await updateDoc(userRef, {
        lastBarcodeUsed: parseInt(newBarcode),
        lastUpdated: new Date()
      });
      
      // Reload books
      await loadBooks();
      
      alert(`✅ Barcode reassigned to ${newBarcode}`);
    } catch (error) {
      console.error('Error reassigning barcode:', error);
      alert('Error reassigning barcode: ' + error.message);
    }
    setReassigning(false);
  };

  const reassignAllDuplicates = async () => {
    const totalToReassign = duplicateGroups.reduce((sum, g) => sum + g.books.length - 1, 0);
    if (!window.confirm(`This will reassign new barcodes to ${totalToReassign} duplicate books (keeping your selected entry for each barcode). Continue?`)) {
      return;
    }
    
    setReassigning(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      let nextBarcode = (userDoc.data()?.lastBarcodeUsed || 125863) + 1;
      
      for (const group of duplicateGroups) {
        // Keep the book at keepIndex, reassign the rest
        const booksToReassign = group.books.filter((_, idx) => idx !== group.keepIndex);
        
        for (const book of booksToReassign) {
          const bookRef = doc(db, 'users', currentUser.uid, 'books', book.id);
          await updateDoc(bookRef, {
            barcode: nextBarcode.toString(),
            barcodeReassignedAt: new Date(),
            previousBarcode: book.barcode
          });
          nextBarcode++;
        }
      }
      
      // Update user's lastBarcodeUsed
      await updateDoc(userRef, {
        lastBarcodeUsed: nextBarcode - 1,
        lastUpdated: new Date()
      });
      
      await loadBooks();
      alert(`✅ All duplicates resolved! Reassigned ${nextBarcode - (userDoc.data()?.lastBarcodeUsed || 125863) - 1} barcodes.`);
    } catch (error) {
      console.error('Error reassigning duplicates:', error);
      alert('Error: ' + error.message);
    }
    setReassigning(false);
  };

  const loadBooks = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const booksRef = collection(db, 'users', currentUser.uid, 'books');
      const booksQuery = query(booksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(booksQuery);
      
      const booksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));

      setBooks(booksData);
    } catch (error) {
      console.error('Error loading books:', error);
    }
    setLoading(false);
  };

  const filterAndSortBooks = () => {
    let filtered = [...books];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(book => 
        book.title?.toLowerCase().includes(term) ||
        book.author?.toLowerCase().includes(term) ||
        book.isbn?.includes(term) ||
        book.barcode?.includes(term) ||
        book.callNumber?.toLowerCase().includes(term)
      );
    }

    // Location filter
    if (filterLocation !== 'all') {
      filtered = filtered.filter(book => book.location === filterLocation);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(book => book.status === filterStatus);
    }

    // Item type filter (books vs ephemera)
    if (filterType === 'books') {
      filtered = filtered.filter(book => book.itemType !== 'EPHEMERA');
    } else if (filterType === 'ephemera') {
      filtered = filtered.filter(book => book.itemType === 'EPHEMERA');
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'author':
          return (a.author || '').localeCompare(b.author || '');
        case 'barcode':
          return (a.barcode || '').localeCompare(b.barcode || '');
        case 'location':
          return (a.location || '').localeCompare(b.location || '');
        default:
          return 0;
      }
    });

    setFilteredBooks(filtered);
  };

  const handleEdit = (book) => {
    setEditingBook({...book});
  };

  const handleSaveEdit = async () => {
    if (!editingBook) return;

    try {
      const bookRef = doc(db, 'users', currentUser.uid, 'books', editingBook.id);
      
      // Prepare update object, filtering out undefined values
      const updateData = {
        barcode: editingBook.barcode || '',
        title: editingBook.title || '',
        subtitle: editingBook.subtitle || '',
        author: editingBook.author || '',
        publisher: editingBook.publisher || '',
        publishDate: editingBook.publishDate || '',
        isbn: editingBook.isbn || '',
        location: editingBook.location || '',
        callNumber: editingBook.callNumber || '',
        ficOrNonfic: editingBook.ficOrNonfic || '',
        deweyDecimal: editingBook.deweyDecimal || '',
        targetAudience: editingBook.targetAudience || '',
        ageRange: editingBook.ageRange || '',
        readingLevel: editingBook.readingLevel || '',
        lexileLevel: editingBook.lexileLevel || '',
        listPrice: editingBook.listPrice ?? 0,
        libraryPrice: editingBook.libraryPrice ?? 0,
        price: editingBook.price ?? 0,
        priceSource: editingBook.priceSource || '',
        description: editingBook.description || '',
        generalNotes: editingBook.generalNotes || [],
        forFansOf: editingBook.forFansOf || [],
        subjects: editingBook.subjects || [],
        genres: editingBook.genres || [],
        notes: editingBook.notes || ''
      };

      await updateDoc(bookRef, updateData);

      setBooks(books.map(book => 
        book.id === editingBook.id ? editingBook : book
      ));
      setEditingBook(null);
      alert('Book updated successfully!');
    } catch (error) {
      console.error('Error updating book:', error);
      alert('Error updating book: ' + error.message);
    }
  };

  const handleDelete = async (bookId) => {
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'books', bookId));
      setBooks(books.filter(book => book.id !== bookId));
      setShowDeleteConfirm(null);
      alert('Book deleted successfully!');
    } catch (error) {
      console.error('Error deleting book:', error);
      alert('Error deleting book: ' + error.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      cataloged: 'bg-blue-100 text-blue-800',
      printed: 'bg-green-100 text-green-800',
      exported: 'bg-purple-100 text-purple-800'
    };
    
    return badges[status] || 'bg-slate-100 text-slate-800';
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="hidden sm:inline text-purple-300">|</span>
              <span className="hidden sm:inline text-purple-200">Allsight</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="text-right">
                <p className="text-sm text-purple-300">Total Books</p>
                <p className="text-xl font-bold">{books.length}</p>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm sm:text-base bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="bg-slate-200 rounded-xl shadow-xl p-6 border border-slate-300">
            <h1 className="text-2xl font-bold mb-4 text-slate-800">🔍 Allsight</h1>
            <p className="text-slate-600 mb-6">Search and manage all books in your collection</p>
            
            {/* Navigation Links */}
            <div className="mb-6 flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/forge')}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                ⚒️ Add Books
              </button>
              <button
                onClick={() => navigate('/runes')}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                📜 Print Labels
              </button>
              <button
                onClick={() => navigate('/bifrost')}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm"
              >
                🌈 Export MARC
              </button>
              <button
                onClick={() => setShowDuplicateChecker(!showDuplicateChecker)}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                  duplicateGroups.length > 0 
                    ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse' 
                    : 'bg-slate-500 text-white hover:bg-slate-600'
                }`}
              >
                🔍 Duplicates {duplicateGroups.length > 0 ? `(${duplicateGroups.length})` : '(0)'}
              </button>
            </div>
            
            {/* Duplicate Checker Section */}
            {showDuplicateChecker && (
              <div className={`mb-6 p-4 rounded-lg border-2 ${
                duplicateGroups.length > 0 
                  ? 'bg-red-50 border-red-300' 
                  : 'bg-green-50 border-green-300'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    {duplicateGroups.length > 0 ? (
                      <>
                        <span className="text-red-600">⚠️ {duplicateGroups.length} Duplicate Barcode{duplicateGroups.length > 1 ? 's' : ''} Found</span>
                      </>
                    ) : (
                      <span className="text-green-600">✅ No Duplicate Barcodes</span>
                    )}
                  </h3>
                  {duplicateGroups.length > 0 && (
                    <button
                      onClick={reassignAllDuplicates}
                      disabled={reassigning}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-slate-400 text-sm"
                    >
                      {reassigning ? '⏳ Fixing...' : '🔧 Fix All Automatically'}
                    </button>
                  )}
                </div>
                
                {duplicateGroups.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Use "⇄ Swap" to choose which book keeps the barcode, then click "Reassign" or "Fix All" to give new barcodes to the others.
                    </p>
                    {duplicateGroups.map((group, groupIndex) => (
                      <div key={group.barcode} className="bg-white rounded-lg p-4 border border-red-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-bold text-red-700">
                            Barcode: <span className="font-mono">{group.barcode}</span> 
                            <span className="text-slate-500 font-normal ml-2">({group.books.length} copies)</span>
                          </div>
                          {group.books.length === 2 && (
                            <button
                              onClick={() => toggleKeepBook(group.barcode)}
                              className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 flex items-center gap-1"
                            >
                              ⇄ Swap
                            </button>
                          )}
                        </div>
                        <div className="space-y-2">
                          {group.books.map((book, bookIndex) => (
                            <div 
                              key={book.id} 
                              className={`flex items-center justify-between p-2 rounded ${
                                bookIndex === group.keepIndex ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                              }`}
                            >
                              <div className="flex-1">
                                <span className="font-medium">{book.title}</span>
                                <span className="text-slate-500 text-sm ml-2">by {book.author}</span>
                                <span className="text-slate-400 text-xs ml-2">
                                  ({book.location} • Added {new Date(book.createdAt).toLocaleDateString()})
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {bookIndex === group.keepIndex ? (
                                  <span className="text-green-600 text-sm font-medium">✓ Keep</span>
                                ) : (
                                  <button
                                    onClick={() => reassignBarcode(book.id)}
                                    disabled={reassigning}
                                    className="bg-orange-500 text-white px-3 py-1 rounded text-sm hover:bg-orange-600 disabled:bg-slate-400"
                                  >
                                    {reassigning ? '...' : 'Reassign'}
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-green-700">All barcodes in your collection are unique. You're good to export!</p>
                )}
              </div>
            )}
            
            {/* Search and Filters */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Search:
                </label>
                <input
                  type="text"
                  placeholder="Title, author, ISBN, barcode, call number..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Type:
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                >
                  <option value="all">All Items</option>
                  <option value="books">Books Only</option>
                  <option value="ephemera">Ephemera Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Location:
                </label>
                <select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                >
                  <option value="all">All Locations</option>
                  {locations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Status:
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="cataloged">Cataloged</option>
                  <option value="printed">Printed</option>
                  <option value="exported">Exported</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Sort By:
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title">Title A-Z</option>
                  <option value="author">Author A-Z</option>
                  <option value="barcode">Barcode</option>
                  <option value="location">Location</option>
                </select>
              </div>
            </div>

            {/* Results Summary */}
            <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg p-4 border border-purple-200">
              <p className="text-purple-800">
                Showing <strong>{filteredBooks.length}</strong> of <strong>{books.length}</strong> {filterType === 'ephemera' ? 'ephemera items' : filterType === 'books' ? 'books' : 'items'}
                {searchTerm && ` matching "${searchTerm}"`}
                {filterLocation !== 'all' && ` in ${filterLocation}`}
                {filterStatus !== 'all' && ` with status ${filterStatus}`}
              </p>
            </div>
          </div>

          {/* Book List */}
          {loading ? (
            <div className="bg-slate-200 rounded-xl shadow-xl p-8 border border-slate-300 text-center">
              <p className="text-slate-600">Loading books...</p>
            </div>
          ) : filteredBooks.length === 0 ? (
            <div className="bg-slate-200 rounded-xl shadow-xl p-8 border border-slate-300 text-center">
              <p className="text-slate-600 mb-4">
                {books.length === 0 
                  ? 'No books in your collection yet.' 
                  : 'No books match your search criteria.'
                }
              </p>
              {books.length === 0 && (
                <button 
                  onClick={() => navigate('/forge')}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
                >
                  Start Cataloging
                </button>
              )}
            </div>
          ) : (
            <div className="bg-slate-200 rounded-xl shadow-xl border border-slate-300">
              <div className="px-6 py-4 border-b border-slate-300 bg-gradient-to-r from-slate-300 to-slate-250 rounded-t-xl">
                <h2 className="text-xl font-bold text-slate-800">Book Collection</h2>
              </div>
              <div className="divide-y divide-slate-300">
                {filteredBooks.map((book) => (
                  <div key={book.id} className="px-6 py-4 hover:bg-slate-150">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-medium text-slate-800 text-lg">{book.title}</h3>
                          {book.itemType === 'EPHEMERA' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                              Ephemera
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(book.status || 'pending')}`}>
                            {book.status || 'pending'}
                          </span>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
                          <div className="space-y-1">
                            <p><strong>Author:</strong> {book.author}</p>
                            <p><strong>Location:</strong> {book.location}</p>
                            <p><strong>Call Number:</strong> <span className="font-mono">{book.callNumber}</span></p>
                          </div>
                          <div className="space-y-1">
                            <p><strong>Barcode:</strong> <span className="font-mono">{book.barcode}</span></p>
                            <p><strong>ISBN:</strong> <span className="font-mono">{book.isbn}</span></p>
                            <p><strong>Added:</strong> {book.createdAt?.toLocaleDateString()}</p>
                          </div>
                        </div>
                        {book.notes && (
                          <p className="text-sm text-slate-600 mt-2">
                            <strong>Notes:</strong> {book.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(book)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(book.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      {editingBook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4">
            <h3 className="text-lg font-bold mb-4">Edit Book: {editingBook.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Barcode</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono"
                  value={editingBook.barcode || ''}
                  onChange={(e) => setEditingBook({...editingBook, barcode: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  value={editingBook.title}
                  onChange={(e) => setEditingBook({...editingBook, title: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Author</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  value={editingBook.author}
                  onChange={(e) => setEditingBook({...editingBook, author: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  value={editingBook.location}
                  onChange={(e) => setEditingBook({...editingBook, location: e.target.value})}
                >
                  {locations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Call Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono"
                  value={editingBook.callNumber}
                  onChange={(e) => setEditingBook({...editingBook, callNumber: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  value={editingBook.price || ''}
                  onChange={(e) => setEditingBook({...editingBook, price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  rows="2"
                  value={editingBook.notes || ''}
                  onChange={(e) => setEditingBook({...editingBook, notes: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingBook(null)}
                className="bg-slate-500 text-white px-4 py-2 rounded-lg hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold mb-4 text-red-800">Confirm Deletion</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete this book? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="bg-slate-500 text-white px-4 py-2 rounded-lg hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Allsight;